/* Unified Admin Case Dashboard.
 *
 * Replaces the 5-page "Orders" maze (New / Inhouse Planning / Outsourced /
 * Inhouse Production / Outsourced Production) with one screen where admin can
 * see every case, filter by stage, drill into one for full view/edit/manage.
 */
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    ClipboardText, Clock, Phone, Storefront, User, Stethoscope, Tray,
    ShieldCheck, UserCheck, Package, CheckCircle, ShareNetwork,
    Factory, Truck, ArrowRight, PaperPlaneRight,
    Warning, ArrowsCounterClockwise, Crown, CaretDown,
} from "@phosphor-icons/react";
import { STATUS_CONFIG, TONE_CLASSES, statusLabel, statusTone, statusGroup, productionStageLabel } from "@/lib/caseStatus";
import { confirmDialog } from "@/lib/confirm";
import BackToBreadcrumb from "@/components/admin/BackToBreadcrumb";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";

/* ---------- Status → stage bucket (used for filter pills + tile counts) ---------- */
const STAGE_BUCKETS = [
    { id: "all",                  label: "All cases",                icon: ClipboardText, color: "primary" },
    { id: "unrouted",             label: "Needs routing",            icon: Tray,          color: "rose",     hint: "Doctor-submitted, not yet assigned" },
    { id: "planning",             label: "In planning",              icon: User,          color: "primary",  hint: "Planner has the case" },
    { id: "self_plan_qa",         label: "Self-Plan QA",             icon: ShieldCheck,   color: "blue",     hint: "QA assigned — awaiting decision" },
    { id: "qa",                   label: "Internal QA",              icon: ShieldCheck,   color: "violet" },
    { id: "with_doctor",          label: "Awaiting doctor approval", icon: UserCheck,     color: "amber" },
    /* iter98.99ch — Dedicated tile for cases the planner has just finalised
       the MFG spec on. These are admin-actionable: pick a production tech
       and route. Separated from the legacy `awaiting_assignment` bucket
       (which still holds Self-Plan cases the doctor finalised) so admins
       can see at a glance how many CoE cases are ready vs. how many
       Self-Plan ones are queued. */
    { id: "mfg_ready",            label: "MFG Spec Ready",           icon: PaperPlaneRight, color: "violet",  hint: "Planner finalised the MFG spec — admin routes to production" },
    { id: "awaiting_assignment",  label: "Awaiting assignment",      icon: PaperPlaneRight, color: "emerald", hint: "Doctor approved (Self-Plan) — admin needs to route to production" },
    { id: "production",           label: "In production",            icon: Factory,       color: "orange",   hint: "Assigned or being manufactured" },
    { id: "shipped",              label: "Aligners shipped",         icon: Truck,         color: "orange",   hint: "Shipped — waiting on doctor to confirm receipt" },
    { id: "treatment",            label: "Aligners received",        icon: CheckCircle,   color: "emerald" },
    { id: "outsourced",           label: "Outsourced",               icon: ShareNetwork,  color: "violet" },
];

/* Map case.status → bucket id. */
function bucketFor(c) {
    const s = c.status;
    if (s === "submitted" && !c.planning_type) return "unrouted";
    // iter98.99am — Self-Plan cases awaiting admin's QA assignment also
    // land in "Needs routing" so the admin's actionable queue is one
    // place. Once they assign a QA, the case flips to in_qa_review and
    // jumps to the new "Self-Plan QA" bucket.
    if (s === "pending_admin_review") return "unrouted";
    if (s === "in_qa_review") return "self_plan_qa";
    // iter97.50 — self-planned cases that the doctor has finalised (status=
    // submitted_to_production) but the admin hasn't yet routed to a specific
    // production tech belong in "Needs routing" too. Prior to this rule they
    // were grouped under "In production" which hid the admin's actionable
    // work — they need to pick a tech / batch the case before manufacturing
    // can pick it up.
    if (s === "submitted_to_production" && c.planning_mode === "doctor" && !c.production_tech_id) return "unrouted";
    if (c.planning_type === "outsourced" && ["submitted", "assigned_to_planner", "in_planning", "ready_for_review", "sent_for_approval"].includes(s)) return "outsourced";
    if (s === "assigned_to_planner" || s === "in_planning" || s === "revision_requested" || s === "info_requested" || s === "approved_pending_mfg") return "planning";
    if (s === "submitted_to_qa") return "qa";
    if (s === "sent_for_approval" || s === "ready_for_review") return "with_doctor";
    if (s === "awaiting_production_assignment") return "mfg_ready";
    if (s === "approved") return "awaiting_assignment";
    if (["assigned_to_production", "in_production", "submitted_to_production"].includes(s)) return "production";
    if (s === "shipped") return "shipped";
    if (s === "in_treatment" || s === "treatment_started" || s === "refinement_requested") return "treatment";
    return null; // completed/cancelled/draft etc. — only shown in "all"
}

const STATUS_THEMES = {
    draft:             { label: "Draft", bg: "bg-zinc-100 text-zinc-700" },
    submitted:         { label: "Submitted", bg: "bg-blue-100 text-blue-800" },
    pending_admin_review: { label: "Pending admin review", bg: "bg-amber-100 text-amber-800" },
    in_qa_review:      { label: "In QA review", bg: "bg-blue-100 text-blue-800" },
    assigned_to_planner:{ label: "Planner pending", bg: "bg-amber-100 text-amber-800" },
    in_planning:       { label: "Planning", bg: "bg-blue-100 text-blue-800" },
    submitted_to_qa:   { label: "Internal QA", bg: "bg-violet-100 text-violet-800" },
    sent_for_approval: { label: "With doctor", bg: "bg-violet-100 text-violet-800" },
    ready_for_review:  { label: "With doctor", bg: "bg-violet-100 text-violet-800" },
    revision_requested:{ label: "Revision", bg: "bg-amber-100 text-amber-800" },
    approved:          { label: "Approved", bg: "bg-emerald-100 text-emerald-800" },
    /* iter98.99ch — CoE planner's MFG-spec authoring window + ready-to-route. */
    approved_pending_mfg:           { label: "MFG Spec Pending",   bg: "bg-amber-100 text-amber-800" },
    awaiting_production_assignment: { label: "MFG Spec Ready",     bg: "bg-violet-100 text-violet-800" },
    assigned_to_production: { label: "Assigned to Production", bg: "bg-amber-100 text-amber-800" },
    in_production:     { label: "Production Started", bg: "bg-orange-100 text-orange-800" },
    shipped:           { label: "Aligners Shipped", bg: "bg-orange-100 text-orange-800" },
    in_treatment:      { label: "Aligners Received", bg: "bg-emerald-100 text-emerald-800" },
    refinement_requested: { label: "Refinement", bg: "bg-amber-100 text-amber-800" },
    completed:         { label: "Completed", bg: "bg-zinc-100 text-zinc-700" },
    cancelled:         { label: "Cancelled", bg: "bg-rose-100 text-rose-700" },
    plan_authoring:    { label: "Self-Plan submitted", bg: "bg-blue-100 text-blue-800" },
};

function timeAgo(iso) {
    if (!iso) return "—";
    const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
}

function slaBucket(iso) {
    if (!iso) return "fresh";
    const hrs = (Date.now() - new Date(iso).getTime()) / 3600000;
    if (hrs < 24) return "fresh";
    if (hrs < 48) return "warn";
    return "breach";
}

/* iter97.30 — Pre-flight readiness check for cases about to enter (or
   already in) production. Mirrors the gates enforced in the Pre-Production
   Gate Modal so admins can spot blockers BEFORE assigning to a tech:
     • STL files: at least one .stl / .obj / .ply attached
     • Manufacturing spec: sheet material id + arches + (stages OR plan
       aligner counts)
   Returns null when the case isn't in a pre-prod phase (we don't badge
   submitted/planning cases — that noise would dilute the signal). */
function preflightStatus(c) {
    if (!c) return null;
    const status = c.status;
    /* Show the badge from "approved" (awaiting assignment) all the way
       through "assigned_to_production" so admins can also retroactively
       fix gaps after assignment if needed. */
    const inScope = ["approved", "assigned_to_production", "submitted_to_production"].includes(status);
    if (!inScope) return null;
    const stl = (c.files || []).filter((f) => {
        const n = (f.filename || f.name || "").toLowerCase();
        return /\.(stl|obj|ply)$/.test(n);
    });
    const stlOk = stl.length > 0;
    const spec = c.manufacturing_spec || {};
    const plan = c.plan || {};
    const hasSheet = !!(spec.aligner_sheet_material_id || spec.aligner_sheet_id);
    const hasArches = !!(spec.arches || c.arches);
    const planU = Number(plan.aligner_count_upper || 0);
    const planL = Number(plan.aligner_count_lower || 0);
    const stages = Number(spec.aligner_stages_count || 0);
    const hasAlignerCount = (planU + planL > 0) || stages > 0;
    const specOk = hasSheet && hasArches && hasAlignerCount;
    return {
        stlOk,
        specOk,
        stlCount: stl.length,
        allOk: stlOk && specOk,
    };
}


/* iter97.30 — Visual pre-flight chip cluster. Renders next to the case
   row's status pill. Green when both checks pass, rose when anything's
   missing. Tooltips explain the underlying gap so admin can act. */
function PreflightBadges({ c }) {
    const pf = preflightStatus(c);
    if (!pf) return null;
    if (pf.allOk) {
        return (
            <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-[9px] bg-emerald-100 text-emerald-800 border border-emerald-200"
                title={`STL ready (${pf.stlCount} file${pf.stlCount === 1 ? "" : "s"}) · Spec complete`}
                data-testid={`admin-case-preflight-${c.id}`}
                data-preflight="ok"
            >
                <CheckCircle size={9} weight="fill" /> Pre-flight ready
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center gap-1.5 flex-wrap"
            data-testid={`admin-case-preflight-${c.id}`}
            data-preflight={!pf.stlOk && !pf.specOk ? "both_missing" : !pf.stlOk ? "no_stl" : "incomplete_spec"}
        >
            {!pf.stlOk && (
                <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-[9px] bg-rose-100 text-rose-800 border border-rose-200"
                    title="No STL / OBJ / PLY files attached. Production cannot start."
                >
                    <Warning size={9} weight="fill" /> Missing STL
                </span>
            )}
            {!pf.specOk && (
                <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-[9px] bg-amber-100 text-amber-800 border border-amber-200"
                    title="Planner spec missing one of: sheet type, arches, aligner stage count."
                >
                    <Warning size={9} weight="fill" /> Incomplete spec
                </span>
            )}
        </span>
    );
}


export default function AdminCases() {
    const [cases, setCases] = useState([]);
    const [planners, setPlanners] = useState([]);
    const [qas, setQas] = useState([]);
    const [shippedAlerts, setShippedAlerts] = useState({ rows: [], stuck_alert_threshold_days: 10, auto_confirm_threshold_days: 14 });
    const [autoConfirmBusy, setAutoConfirmBusy] = useState(false);
    const [q, setQ] = useState("");
    /* iter100.241 — Sync `bucket` filter with the `?bucket=<id>` URL param
       so drill-downs from Overview (TAT tiles) and Analytics (KPI tiles)
       deep-link into the right stage view. Also keeps the URL in sync when
       admin changes the filter here so the back button behaves naturally. */
    const [searchParams, setSearchParams] = useSearchParams();
    const validBucketIds = React.useMemo(() => new Set(STAGE_BUCKETS.map((b) => b.id)), []);
    const initialBucket = (() => {
        const b = searchParams.get("bucket");
        return b && validBucketIds.has(b) ? b : "all";
    })();
    const [bucket, setBucket] = useState(initialBucket);
    useEffect(() => {
        const current = searchParams.get("bucket");
        if (bucket === "all") {
            if (current) {
                searchParams.delete("bucket");
                setSearchParams(searchParams, { replace: true });
            }
        } else if (current !== bucket) {
            searchParams.set("bucket", bucket);
            setSearchParams(searchParams, { replace: true });
        }
    }, [bucket, searchParams, setSearchParams]);
    const [planningType, setPlanningType] = useState("any"); // any | inhouse | outsourced
    /* Planning mode filter — separates COE-planned vs doctor-planned (bring
       your own plan) cases. Useful for ops: planner team only cares about
       coe; doctor-planned cases skip COE entirely and go straight to lab. */
    const [planningMode, setPlanningMode] = useState("any"); // any | coe | doctor
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/admin/cases"),
            api.get("/admin/staff"),
            api.get("/admin/cases/shipped-alerts").then((r) => r.data).catch(() => ({ rows: [], stuck_alert_threshold_days: 10, auto_confirm_threshold_days: 14 })),
        ]).then(([c, s, alerts]) => {
            setCases(c.data);
            setPlanners(s.data.filter((x) => x.staff_type === "planner" && x.is_active !== false));
            // iter98.99am — Pick out QA staff for the Self-Plan
            // pending_admin_review → in_qa_review inline assigner.
            setQas(s.data.filter((x) => x.staff_type === "qa" && x.is_active !== false));
            setShippedAlerts(alerts);
        }).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* Stuck shipment summary — number of cases sitting in `shipped` longer
       than the ops threshold (10d warn / 14d auto-confirm). */
    const shippedSummary = useMemo(() => {
        const rows = shippedAlerts.rows || [];
        const stuck = rows.filter((r) => r.flag === "stuck").length;
        const dueAuto = rows.filter((r) => r.flag === "will_auto_confirm").length;
        return { total: rows.length, stuck, dueAuto };
    }, [shippedAlerts]);

    const runAutoConfirm = async () => {
        if (!(await confirmDialog({ title: `Auto-confirm ${shippedSummary.dueAuto} shipment${shippedSummary.dueAuto === 1 ? "" : "s"} that have been waiting > ${shippedAlerts.auto_confirm_threshold_days} days?`, confirmText: "Auto-confirm", tone: "warn" }))) return;
        setAutoConfirmBusy(true);
        try {
            const res = await api.post("/admin/cases/auto-confirm-stale-shipments");
            toast.success(`${res.data.auto_confirmed_count || 0} case${res.data.auto_confirmed_count === 1 ? "" : "s"} auto-confirmed`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to run auto-confirm");
        } finally {
            setAutoConfirmBusy(false);
        }
    };

    /* Tile counts derived in one pass. */
    const counts = useMemo(() => {
        const map = { all: cases.length };
        STAGE_BUCKETS.forEach((b) => { if (b.id !== "all") map[b.id] = 0; });
        cases.forEach((c) => {
            const b = bucketFor(c);
            if (b) map[b] = (map[b] || 0) + 1;
        });
        return map;
    }, [cases]);

    const filtered = useMemo(() => {
        let list = cases;
        if (bucket !== "all") list = list.filter((c) => bucketFor(c) === bucket);
        if (planningType !== "any") list = list.filter((c) => c.planning_type === planningType);
        if (planningMode !== "any") {
            // Legacy cases without the field default to "coe"
            list = list.filter((c) => (c.planning_mode || "coe") === planningMode);
        }
        if (q.trim()) {
            const l = q.trim().toLowerCase();
            list = list.filter((c) =>
                (c.code || "").toLowerCase().includes(l) ||
                (c.patient_name || "").toLowerCase().includes(l) ||
                (c.doctor_name || "").toLowerCase().includes(l) ||
                (c.planner_name || "").toLowerCase().includes(l) ||
                (c.practice_location_name || "").toLowerCase().includes(l) ||
                (c.diagnosis || "").toLowerCase().includes(l)
            );
        }
        return list;
    }, [cases, q, bucket, planningType, planningMode]);

    return (
        <div data-testid="admin-cases-page" className="space-y-6">
            {/* iter100.241 — Context-preserving breadcrumb. Renders only when
                the admin arrived via a drill-down (Overview TAT / Analytics
                KPI tile). Silent no-op otherwise. */}
            <BackToBreadcrumb />

            {/* HEADER — iter101.27 · Aligned to /admin/patients + /admin/appointments
                grammar (text-2xl H1 + text-xs subtitle, no hero card wrapper).
                Removed: big rounded card, "PLANNING MIX" bar widget, and the
                duplicate 6-tile row that repeated what the FilterChipRow shows.
                Keeps the page focused on the routing surface. */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Operations · Aligner Cases</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Aligner Cases</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Every aligner case across the Invisique network. Filter by stage, drill in to view, edit or route.
                    </p>
                </div>
            </div>

            {/* KPI STRIP — 5 hero tiles mirroring /admin/patients cockpit.
                Each tile is a one-tap drill-into-bucket shortcut. Focused
                on stages that most often need admin attention. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <CaseStatTile
                    testid="admin-cases-kpi-total" label="Total cases" value={counts.all || 0}
                    Icon={ClipboardText} tone="primary" hint="Across the network"
                    onClick={() => setBucket("all")}
                />
                <CaseStatTile
                    testid="admin-cases-kpi-unrouted" label="Needs routing" value={counts.unrouted || 0}
                    Icon={Tray} tone="rose" hint="Awaiting planner routing"
                    onClick={() => setBucket("unrouted")}
                />
                <CaseStatTile
                    testid="admin-cases-kpi-planning" label="In planning" value={counts.planning || 0}
                    Icon={User} tone="primary" hint="With the planning team"
                    onClick={() => setBucket("planning")}
                />
                <CaseStatTile
                    testid="admin-cases-kpi-with-doctor" label="With doctor" value={counts.with_doctor || 0}
                    Icon={UserCheck} tone="amber" hint="Awaiting doctor approval"
                    onClick={() => setBucket("with_doctor")}
                />
                <CaseStatTile
                    testid="admin-cases-kpi-production" label="In production" value={counts.production || 0}
                    Icon={Factory} tone="orange" hint="Being manufactured"
                    onClick={() => setBucket("production")}
                />
            </div>

            {/* SHIPPED ALERTS — surfaces cases that have been sitting in the
                "shipped" bucket beyond the ops thresholds. The cron runs in
                /api/admin/cases/auto-confirm-stale-shipments and admin can
                trigger it manually from here. */}
            {(shippedSummary.stuck > 0 || shippedSummary.dueAuto > 0) && (
                <div
                    className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
                    data-testid="admin-cases-shipped-alert"
                >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-amber-200/70 flex items-center justify-center shrink-0">
                            <Warning size={18} weight="fill" className="text-amber-800" />
                        </div>
                        <div className="min-w-0">
                            <div className="font-display font-bold text-sm sm:text-base text-amber-900">
                                {shippedSummary.dueAuto > 0
                                    ? `${shippedSummary.dueAuto} shipment${shippedSummary.dueAuto === 1 ? "" : "s"} ready for auto-confirm`
                                    : `${shippedSummary.stuck} stuck shipment${shippedSummary.stuck === 1 ? "" : "s"}`}
                            </div>
                            <div className="text-xs text-amber-900/80 mt-0.5">
                                {shippedSummary.stuck > 0 && (
                                    <span>{shippedSummary.stuck} aging past {shippedAlerts.stuck_alert_threshold_days}d</span>
                                )}
                                {shippedSummary.stuck > 0 && shippedSummary.dueAuto > 0 && <span> · </span>}
                                {shippedSummary.dueAuto > 0 && (
                                    <span>{shippedSummary.dueAuto} past {shippedAlerts.auto_confirm_threshold_days}d (will auto-confirm)</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                        <Button
                            variant="outline"
                            className="rounded-full bg-white"
                            onClick={() => setBucket("shipped")}
                            data-testid="admin-cases-shipped-alert-view"
                        >
                            View shipments
                        </Button>
                        {shippedSummary.dueAuto > 0 && (
                            <Button
                                className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                                onClick={runAutoConfirm}
                                disabled={autoConfirmBusy}
                                data-testid="admin-cases-run-auto-confirm"
                            >
                                <CheckCircle size={14} className="mr-1.5" weight="fill" />
                                {autoConfirmBusy ? "Running…" : "Run auto-confirm"}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* FILTER STRIP — iter102.04 · Matched 1:1 with /admin/providers
                (Doctors.jsx). Plain default FilterSearchBar (pill wrapper,
                tinted input) in a `flex flex-col gap-3` container with the
                FilterChipRow below. No sticky wrapper, no negative margins,
                no `bare` prop. */}
            <div className="flex flex-col gap-3" data-testid="admin-cases-filter-bar">
                <FilterSearchBar
                    query={q}
                    setQuery={setQ}
                    placeholder="Search case code, patient, doctor, planner…"
                    searchTestid="admin-cases-search"
                    testid="admin-cases-filter-search"
                    rightSlot={(
                        <>
                            <div className="relative">
                                <select
                                    value={planningType}
                                    onChange={(e) => setPlanningType(e.target.value)}
                                    className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer"
                                    data-testid="admin-cases-ptype-select"
                                    aria-label="Filter by planning type"
                                >
                                    <option value="any">Any planning</option>
                                    <option value="inhouse">In-house</option>
                                    <option value="outsourced">Outsourced</option>
                                </select>
                                <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                            </div>
                            <div className="relative">
                                <select
                                    value={planningMode}
                                    onChange={(e) => setPlanningMode(e.target.value)}
                                    className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer"
                                    data-testid="admin-cases-pmode-select"
                                    aria-label="Filter by plan authorship"
                                >
                                    <option value="any">All plans</option>
                                    <option value="coe">COE-planned</option>
                                    <option value="doctor">Doctor-planned</option>
                                </select>
                                <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                            </div>
                        </>
                    )}
                />
                <FilterChipRow
                    chips={STAGE_BUCKETS.map((b) => ({
                        id: b.id,
                        label: b.label,
                        count: counts[b.id] || 0,
                        tone: b.color === "orange" ? "amber"
                              : b.color === "blue" ? "sky"
                              : (["primary","amber","rose","emerald","sky","slate","violet"].includes(b.color) ? b.color : "primary"),
                    }))}
                    value={bucket}
                    onChange={setBucket}
                    testid="admin-cases-chip-row"
                    chipTestidPrefix="admin-cases-pill"
                />
            </div>

            {/* BODY */}
            {loading ? (
                <div className="text-muted-foreground text-sm text-center py-10">Loading cases…</div>
            ) : filtered.length === 0 ? (
                <Empty
                    isFiltered={q.length > 0 || bucket !== "all" || planningType !== "any"}
                    onClear={() => { setQ(""); setBucket("all"); setPlanningType("any"); }}
                />
            ) : (
                <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="admin-cases-list">
                    <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                        <div className="col-span-2">Case</div>
                        <div className="col-span-2">Patient</div>
                        <div className="col-span-2">Provider</div>
                        <div className="col-span-2">Stage</div>
                        <div className="col-span-2">Planner</div>
                        <div className="col-span-2">Prod tech</div>
                    </div>
                    {filtered.map((c) => (
                        <CaseRow
                            key={c.id}
                            c={c}
                            planners={planners}
                            qas={qas}
                            onChange={load}
                        />
                    ))}
                </div>
            )}

            {/* iter101.26 — Analytics block (Production KPIs + Cancellation
                Reasons) MOVED to /admin/analytics?tab=aligner_cases so
                every operational analytics view lives in one place.
                The Aligner Cases page now stays laser-focused on the
                routing surface admins land here to work on. */}
        </div>
    );
}

/* ---------- KPI Stat tile (mirrors AdminPatients StatTile) ---------- */
function CaseStatTile({ testid, label, value, Icon, tone, hint, onClick }) {
    const accent = tone === "emerald" ? "text-emerald-700 bg-emerald-100"
        : tone === "violet" ? "text-violet-700 bg-violet-100"
        : tone === "amber" ? "text-amber-700 bg-amber-100"
        : tone === "rose" ? "text-rose-700 bg-rose-100"
        : tone === "orange" ? "text-orange-700 bg-orange-100"
        : "text-primary bg-primary/10";
    const Component = onClick ? "button" : "div";
    return (
        <Component
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`flex items-start gap-3 rounded-xl bg-card border border-border p-3 text-left transition ${onClick ? "hover:border-primary/40 hover:shadow-sm cursor-pointer" : ""}`}
            data-testid={testid}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1">{value}</div>
                {hint && <div className="text-[10px] text-muted-foreground mt-1 truncate">{hint}</div>}
            </div>
        </Component>
    );
}

/* ---------- Hero tile (clickable, doubles as quick filter) ---------- */
/* iter101.28 — Row chip primitives.
   PriorityChip: shows all three tiers (Standard/Priority/VIP + any custom
   tier codes) with a distinct tone per tier so admins scan urgency at a
   glance. Standard stays neutral (slate) so it doesn't shout; Priority
   goes amber; VIP/rush goes rose.
   AgeChip: converts case created_at into a color-tinted chip (green
   <24h, amber 24-72h, rose >72h). Doubles as the SLA freshness signal
   the old "X ago" line only hinted at. */
function PriorityChip({ code, label }) {
    const c = (code || "").toLowerCase();
    const displayLabel = label || (c ? c.charAt(0).toUpperCase() + c.slice(1) : "Standard");
    let tone = "slate";
    if (c === "vip" || c === "rush" || c === "urgent") tone = "rose";
    else if (c === "priority" || c === "express" || c === "high") tone = "amber";
    else if (c === "standard" || c === "" || !c) tone = "slate";
    else tone = "violet";  // custom tiers fall into violet so they still stand out
    const cls = {
        slate:  "bg-slate-100 text-slate-700 border-slate-200",
        amber:  "bg-amber-100 text-amber-800 border-amber-200",
        rose:   "bg-rose-100 text-rose-800 border-rose-200",
        violet: "bg-violet-100 text-violet-800 border-violet-200",
    }[tone];
    return (
        <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-[9px] border ${cls}`}
            title={`Priority tier: ${displayLabel}`}
            data-testid="admin-case-row-priority"
        >
            {displayLabel}
        </span>
    );
}

function AgeChip({ iso }) {
    if (!iso) return null;
    const hours = Math.max(0, (Date.now() - new Date(iso).getTime()) / 3600000);
    const cls = hours < 24 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : hours < 72 ? "bg-amber-50 text-amber-800 border-amber-200"
        : "bg-rose-50 text-rose-800 border-rose-200";
    const display = hours < 1 ? `${Math.max(1, Math.round(hours * 60))}m`
        : hours < 24 ? `${Math.round(hours)}h`
        : `${Math.round(hours / 24)}d`;
    return (
        <span
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded font-semibold text-[9px] border ${cls}`}
            title={`Case created ${timeAgo(iso)} ago`}
            data-testid="admin-case-row-age"
        >
            <Clock size={9} weight="fill" /> {display}
        </span>
    );
}

/* ---------- Single case row ---------- */
function CaseRow({ c, planners, qas = [], onChange }) {
    /* Status pill comes from the central dictionary (STATUS_CONFIG) so labels
       stay consistent across every view (Admin / Planner / QA / Doctor / Prod). */
    const label = statusLabel(c.status);
    const toneCls = TONE_CLASSES[statusTone(c.status)] || TONE_CLASSES.slate;
    const bucket = c.status === "submitted" ? slaBucket(c.created_at) : null;
    const codeCls = bucket === "breach" ? "text-rose-700"
        : bucket === "warn" ? "text-amber-700"
        : "text-primary";

    /* iter102.00 — Row-level navigation. Clicking anywhere on the row
       (except explicitly interactive children) opens the case detail.
       Inner nav Links + inline popover buttons stopPropagation so the
       row's onClick doesn't double-fire. */
    const navigate = useNavigate();
    const goToCase = () => navigate(`/admin/cases/${c.id}`);
    const stop = (e) => e.stopPropagation();

    /* Row-level inline pickers gated by case state + planning mode:
        - Submitted + CoE (default) + no planning_type yet → inline "Assign Planner"
        - Self-Plan (doctor) submitted for admin review → inline "Assign QA"
        - Everything else routes through the case detail page — including
          production-tech assignment, which lives inside the case detail's
          "Route to Production" hero banner (iter101.7). Prior code had an
          inline "Route to Production" button on the row + a batch-select
          checkbox; both were removed in iter101.9 at the user's request
          ("i dont want this bulk selection stuff. let the admin click on
          the case and enter the case and then assign the Production Tech"). */
    const isSelfPlan = c.planning_mode === "doctor";
    const needsPlannerAssignment = c.status === "submitted" && !c.planning_type && !isSelfPlan;
    // iter101.50 — QA Reviewer requirement retired from the active
    // pipeline. Self-Plan cases in pending_admin_review no longer
    // need a QA assignment nudge on the list row.
    const needsQaAssignment = false;

    return (
        <div
            role="link"
            tabIndex={0}
            onClick={goToCase}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goToCase();
                }
            }}
            className="relative grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 hover:bg-secondary/30 focus:bg-secondary/40 focus:outline-none transition items-center cursor-pointer"
            data-testid={`admin-case-row-${c.id}`}
            title="Open case detail"
        >
            <div className="md:col-span-2 min-w-0 flex flex-col">
                <span className={`font-mono text-xs font-bold truncate ${codeCls}`}>{c.code || c.id?.slice(0, 8)}</span>
                {/* iter101.28 — Compact chip cluster so admin can scan
                    Priority + Age without opening the drawer. Priority is
                    always shown (all 3 tiers per user direction). Age uses
                    an SLA-colored pill so 3-day-old cases stand out.
                    Planner-avatar is intentionally NOT duplicated here
                    since the row already has a dedicated Planner column. */}
                <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <PriorityChip code={c.priority || c.pricing?.priority_code} label={c.pricing?.priority_label} />
                    <AgeChip iso={c.created_at} />
                </div>
            </div>

            <div className="md:col-span-2 min-w-0 flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                    <User size={13} weight="duotone" />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{c.patient_name || "Patient"}</div>
                    <div className="text-[10px] text-muted-foreground flex items-center gap-1 truncate">
                        <Phone size={9} /> {c.patient_phone || "—"}
                    </div>
                </div>
            </div>

            <div className="md:col-span-2 min-w-0 flex items-center gap-1.5 text-sm">
                <Stethoscope size={13} className="text-primary shrink-0" />
                <div className="min-w-0">
                    <div className="truncate font-medium text-xs">{c.doctor_name || "—"}</div>
                    {c.practice_location_name && (
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1 truncate">
                            <Storefront size={9} /> {c.practice_location_name}
                        </div>
                    )}
                </div>
            </div>

            <div className="md:col-span-2 flex items-center gap-1.5 flex-wrap">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md border font-bold uppercase tracking-wider text-[9px] ${toneCls}`}>
                    {label}
                </span>
                {/* Granular production stage chip — only when the case is in
                    active manufacturing. Surfaces the current micro-stage
                    (Digital Prep / 3D Printing / Thermoforming / QC Review /
                    Packaging / …) without flattening it into the top-level
                    status pill. Visible to admin only — doctors/patients
                    still see the top-level "Production Started" via their
                    own portals. */}
                {c.status === "in_production" && c.production_stage && (
                    <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-[9px] bg-violet-100 text-violet-800 border border-violet-200"
                        title={`Currently in ${productionStageLabel(c.production_stage)}`}
                        data-testid={`admin-case-prod-stage-${c.id}`}
                    >
                        <Factory size={9} weight="fill" /> {productionStageLabel(c.production_stage)}
                    </span>
                )}
                {/* Kind chip — refinements / retainers spawn into the same
                    routing queue as primary cases, so the counsellor needs
                    a glance-cue to triage. Primary cases stay clean (no chip). */}
                {c.case_kind === "refinement" && (
                    <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-[9px] bg-amber-100 text-amber-800 border border-amber-200"
                        title={c.parent_case_code ? `Refinement of ${c.parent_case_code}` : "Refinement child case"}
                        data-testid={`admin-case-kind-${c.id}`}
                    >
                        <ArrowsCounterClockwise size={9} weight="fill" /> Refinement
                    </span>
                )}
                {c.case_kind === "retainer" && (
                    <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-[9px] bg-violet-100 text-violet-800 border border-violet-200"
                        title={c.parent_case_code ? `Retainer for ${c.parent_case_code}` : "Retainer child case"}
                        data-testid={`admin-case-kind-${c.id}`}
                    >
                        <Crown size={9} weight="fill" /> Retainer
                    </span>
                )}
                {c.planning_type && (
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
                        · {c.planning_type === "inhouse" ? "In-house" : "Outsourced"}
                    </span>
                )}
            </div>

            {/* ── PLANNER column (iter101.10) ────────────────────────────────
                Dedicated column so admin can see the planner assignment at a
                glance and reassign inline without opening the case.
                State machine:
                  • Self-Plan case → "Self-plan" chip (doctor is their own planner)
                  • Needs planner (CoE + no planning_type + submitted)
                      → InlineAssign picker (existing component)
                  • Already assigned → planner name + in-house/outsourced tag
                  • Otherwise (pre-submission / cancelled) → em dash
                iter102.00 — Column wrapper stops click propagation so the
                InlineAssign popover + planner-profile link don't also fire
                the row-level nav. */}
            <div
                className="md:col-span-2 min-w-0 flex items-center text-xs"
                data-testid={`admin-case-planner-${c.id}`}
                onClick={stop}
                onKeyDown={stop}
            >
                {isSelfPlan ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-50 text-teal-800 border border-teal-200 text-[10px] font-semibold uppercase tracking-wider">
                        Self-plan
                    </span>
                ) : needsPlannerAssignment ? (
                    <InlineAssign caseId={c.id} planners={planners} onAssigned={onChange} />
                ) : c.planner_name ? (
                    /* iter101.30 — Universal Profile Access. Name text
                       becomes a link to the CoE Member Profile so admins
                       one-tap to see who's on point + their throughput
                       stats. */
                    c.planner_id ? (
                        <Link
                            to={`/admin/coe/people/${c.planner_id}`}
                            className="min-w-0 group"
                            data-testid={`admin-case-planner-link-${c.id}`}
                        >
                            <div className="font-medium text-xs truncate group-hover:text-primary group-hover:underline transition">
                                {c.planner_name.replace(/\s*\(.*?\)$/, "")}
                            </div>
                            {c.planning_type && (
                                <div className="text-[10px] text-muted-foreground truncate">
                                    {c.planning_type === "inhouse" ? "In-house" : "Outsourced"}
                                </div>
                            )}
                        </Link>
                    ) : (
                        <div className="min-w-0">
                            <div className="font-medium text-xs truncate">{c.planner_name.replace(/\s*\(.*?\)$/, "")}</div>
                            {c.planning_type && (
                                <div className="text-[10px] text-muted-foreground truncate">
                                    {c.planning_type === "inhouse" ? "In-house" : "Outsourced"}
                                </div>
                            )}
                        </div>
                    )
                ) : (
                    <span className="text-muted-foreground text-[11px]">—</span>
                )}
            </div>

            {/* ── PROD TECH column (iter101.28) ────────────────────────────
                Replaced the QA column at user's request. QA is only
                meaningful for self-plan cases (a minority) and its
                assignment already surfaces inline elsewhere in the row.
                Prod-tech, in contrast, is the "current owner" once a
                case is past MFG spec — so it's far more useful info
                at row-level for the routing surface.
                State machine:
                  • Assigned → tech name (violet accent, matches tone)
                  • Case is `awaiting_production_assignment` (planner
                    has finalised MFG spec but admin hasn't routed yet)
                    → amber "Needs assignment" pill (row-level click
                    already opens the case-detail Route-to-Production
                    banner, so no need for a nested Link here)
                  • Otherwise → em dash */}
            <div
                className="md:col-span-2 min-w-0 flex items-center text-xs"
                data-testid={`admin-case-prodtech-${c.id}`}
                onClick={stop}
                onKeyDown={stop}
            >
                {c.production_tech_name ? (
                    /* iter101.30 — Universal Profile Access: 1-click hop
                       to the prod-tech's profile from any case row. */
                    c.production_tech_id ? (
                        <Link
                            to={`/admin/coe/people/${c.production_tech_id}`}
                            className="min-w-0 group"
                            data-testid={`admin-case-prodtech-link-${c.id}`}
                        >
                            <div className="font-medium text-violet-800 text-xs truncate group-hover:underline transition">
                                {c.production_tech_name.replace(/\s*\(.*?\)$/, "")}
                            </div>
                            <div className="text-[10px] text-muted-foreground">Production tech</div>
                        </Link>
                    ) : (
                        <div className="min-w-0">
                            <div className="font-medium text-violet-800 text-xs truncate">{c.production_tech_name.replace(/\s*\(.*?\)$/, "")}</div>
                            <div className="text-[10px] text-muted-foreground">Production tech</div>
                        </div>
                    )
                ) : c.status === "awaiting_production_assignment" ? (
                    <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-semibold uppercase tracking-wider"
                        title="Case ready — click the row to open detail and assign a production tech"
                        data-testid={`admin-case-assign-prodtech-${c.id}`}
                    >
                        Needs assignment
                    </span>
                ) : c.qa_name && needsQaAssignment ? (
                    // Rare edge: self-plan case that still needs QA — keep
                    // the inline QA picker on the row when Prod-Tech isn't
                    // yet meaningful so admin can act without navigating.
                    <InlineAssignQA caseId={c.id} qas={qas} onAssigned={onChange} />
                ) : (
                    <span className="text-muted-foreground text-[11px]">—</span>
                )}
            </div>
        </div>
    );
}

/* Inline planner picker (button → popover). */
function InlineAssign({ caseId, planners, onAssigned }) {
    const [open, setOpen] = useState(false);
    const [menuCoords, setMenuCoords] = useState(null);
    const ref = React.useRef(null);
    const btnRef = React.useRef(null);
    const menuRef = React.useRef(null);
    React.useEffect(() => {
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)
                && menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    /* iter100.93 — Anchor the dropdown to the trigger via a body-level
       portal (react-dom `createPortal`). Prior implementation used
       `absolute` positioning inside the row, which was clipped by the
       admin cases list container's `overflow-hidden` when the trigger
       sat on the last few rows of the list — reproduced by the user on
       production. Portalling + fixed positioning + a viewport-clamp
       (never let the menu spill off the right edge) makes the dropdown
       clip-proof regardless of ancestor overflow rules. */
    React.useEffect(() => {
        if (!open || !btnRef.current) return undefined;
        const compute = () => {
            const r = btnRef.current.getBoundingClientRect();
            const menuW = 240;
            const padding = 8;
            const left = Math.max(padding, Math.min(r.right - menuW, window.innerWidth - menuW - padding));
            const top = r.bottom + 4;
            setMenuCoords({ top, left, width: menuW });
        };
        compute();
        window.addEventListener("resize", compute);
        window.addEventListener("scroll", compute, true);
        return () => {
            window.removeEventListener("resize", compute);
            window.removeEventListener("scroll", compute, true);
        };
    }, [open]);

    const assign = async (planner) => {
        setOpen(false);
        try {
            await api.put(`/admin/cases/${caseId}/assign-planner`, { planner_id: planner.id });
            toast.success(`Routed to ${planner.name}`);
            onAssigned?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const outsource = async () => {
        setOpen(false);
        try {
            await api.put(`/admin/cases/${caseId}/planning-type`, { planning_type: "outsourced" });
            toast.success("Marked for outsourcing");
            onAssigned?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="relative" ref={ref}>
            {/* Sky/blue tone differentiates the planner-routing action from
               the green production-assignment CTA and from the amber
               "blocked" warning state on production buttons. The label
               explicitly says "Planner" so there's no ambiguity about
               who the case is being sent to. */}
            <Button
                ref={btnRef}
                size="sm"
                variant="outline"
                onClick={() => setOpen((v) => !v)}
                className="h-8 rounded-full bg-sky-50 border-sky-300 text-sky-800 hover:bg-sky-100 font-semibold"
                data-testid={`assign-${caseId}`}
            >
                <PaperPlaneRight size={11} className="mr-1" weight="fill" />
                Assign Planner ▾
            </Button>
            {open && menuCoords && createPortal(
                <div
                    ref={menuRef}
                    className="fixed z-[60] bg-card border border-border rounded-xl shadow-lg overflow-hidden"
                    style={{ top: menuCoords.top, left: menuCoords.left, width: menuCoords.width }}
                    data-testid={`assign-menu-${caseId}`}
                >
                    {planners.length > 0 && (
                        <div className="px-3 py-1.5 text-[9px] uppercase tracking-[0.18em] text-muted-foreground bg-secondary/50 border-b border-border">
                            Assign planner
                        </div>
                    )}
                    {planners.map((p) => (
                        <button key={p.id} type="button" onClick={() => assign(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/50 border-b last:border-0 border-border" data-testid={`assign-${caseId}-${p.id}`}>
                            <div className="font-medium">{p.name}</div>
                            <div className="text-[10px] text-muted-foreground">
                                {p.phone}{typeof p.active_case_count === "number" ? ` · ${p.active_case_count} active` : ""}
                            </div>
                        </button>
                    ))}
                    <button type="button" onClick={outsource} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/50 text-violet-700 font-medium" data-testid={`outsource-${caseId}`}>
                        <ShareNetwork size={12} className="inline mr-1" /> Outsource instead
                    </button>
                </div>,
                document.body,
            )}
        </div>
    );
}

/* iter98.99am — Inline QA picker for Self-Plan cases in pending_admin_review.
   Mirrors InlineAssign's shape: a pill button + popover with the active QA
   roster. Clicking a row PUTs /admin/cases/{id}/assign-qa, which flips
   status to in_qa_review server-side and notifies the QA. */
function InlineAssignQA({ caseId, qas, onAssigned }) {
    const [open, setOpen] = useState(false);
    const [menuCoords, setMenuCoords] = useState(null);
    const ref = React.useRef(null);
    const btnRef = React.useRef(null);
    const menuRef = React.useRef(null);
    React.useEffect(() => {
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)
                && menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    // iter100.93 — same portal-based clip-proof positioning as InlineAssign.
    React.useEffect(() => {
        if (!open || !btnRef.current) return undefined;
        const compute = () => {
            const r = btnRef.current.getBoundingClientRect();
            const menuW = 240;
            const padding = 8;
            const left = Math.max(padding, Math.min(r.right - menuW, window.innerWidth - menuW - padding));
            const top = r.bottom + 4;
            setMenuCoords({ top, left, width: menuW });
        };
        compute();
        window.addEventListener("resize", compute);
        window.addEventListener("scroll", compute, true);
        return () => {
            window.removeEventListener("resize", compute);
            window.removeEventListener("scroll", compute, true);
        };
    }, [open]);

    const assign = async (qa) => {
        setOpen(false);
        try {
            await api.put(`/admin/cases/${caseId}/assign-qa`, { qa_id: qa.id });
            toast.success(`Routed to ${qa.name} for QA review`);
            onAssigned?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="relative" ref={ref}>
            <Button
                ref={btnRef}
                size="sm"
                variant="outline"
                onClick={() => setOpen((v) => !v)}
                className="h-8 rounded-full bg-blue-50 border-blue-300 text-blue-800 hover:bg-blue-100 font-semibold"
                data-testid={`assign-qa-${caseId}`}
            >
                <ShieldCheck size={11} className="mr-1" weight="fill" />
                Assign QA ▾
            </Button>
            {open && menuCoords && createPortal(
                <div
                    ref={menuRef}
                    className="fixed z-[60] bg-card border border-border rounded-xl shadow-lg overflow-hidden"
                    style={{ top: menuCoords.top, left: menuCoords.left, width: menuCoords.width }}
                    data-testid={`assign-qa-menu-${caseId}`}
                >
                    {qas.length > 0 ? (
                        <>
                            <div className="px-3 py-1.5 text-[9px] uppercase tracking-[0.18em] text-muted-foreground bg-secondary/50 border-b border-border">
                                Assign QA reviewer
                            </div>
                            {qas.map((q) => (
                                <button
                                    key={q.id}
                                    type="button"
                                    onClick={() => assign(q)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/50 border-b last:border-0 border-border"
                                    data-testid={`assign-qa-${caseId}-${q.id}`}
                                >
                                    <div className="font-medium">{q.name}</div>
                                    <div className="text-[10px] text-muted-foreground">
                                        {q.phone}{typeof q.active_case_count === "number" ? ` · ${q.active_case_count} active` : ""}
                                    </div>
                                </button>
                            ))}
                        </>
                    ) : (
                        <div className="px-3 py-3 text-[12px] text-muted-foreground italic">
                            No active QA reviewers. Add a QA from Team settings first.
                        </div>
                    )}
                </div>,
                document.body,
            )}
        </div>
    );
}


function Empty({ isFiltered, onClear }) {
    return (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid="admin-cases-empty">
            <ClipboardText size={28} className="mx-auto text-muted-foreground mb-3" />
            <div className="font-display text-lg font-bold">
                {isFiltered ? "No cases match your filters" : "No cases yet"}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">
                {isFiltered
                    ? "Try clearing your search or stage filter."
                    : "Cases submitted by partner doctors will appear here automatically."}
            </p>
            {isFiltered && (
                <Button variant="outline" className="rounded-full mt-5" onClick={onClear} data-testid="admin-cases-empty-clear">
                    Clear filters
                </Button>
            )}
        </div>
    );
}
